import { useState } from 'react'

import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { InsuranceIndicationStatus, Vendor } from '@/models/insuranceTypes'
import type { CarrierCard } from '@/routes/Public/carrierCard'
import { CarrierResultCard } from '@/routes/Public/CarrierResultCard'
import { Demo, DsSection } from '../primitives'

// Mirrors backend/insure/models/insurance.py CARRIER_LOGO_MAP — the design
// system should show the same static.cai.insure images the product actually
// serves, not local marketing-site assets.
const CARRIER_LOGO_BASE = 'https://static.cai.insure/carriers'
const CARRIER_LOGOS = {
  biberk: { url: `${CARRIER_LOGO_BASE}/biberk-icon.png`, alt: 'Biberk' },
  canal: { url: `${CARRIER_LOGO_BASE}/canal-icon.png`, alt: 'Canal' },
  coverwhale: { url: `${CARRIER_LOGO_BASE}/coverwhale-icon.png`, alt: 'Cover Whale' },
  geico: { url: `${CARRIER_LOGO_BASE}/geico-icon.png`, alt: 'Geico' },
  hanover: { url: `${CARRIER_LOGO_BASE}/hanover-icon.png`, alt: 'Hanover' },
  nirvana: { url: `${CARRIER_LOGO_BASE}/nirvana-icon.png`, alt: 'Nirvana' },
  progressive: { url: `${CARRIER_LOGO_BASE}/progressive-icon.png`, alt: 'Progressive' },
}

function mockCard(overrides: Partial<CarrierCard>): CarrierCard {
  return {
    id: overrides.id ?? 'mock',
    vendor: Vendor.GEICO,
    vendorName: 'Carrier',
    status: InsuranceIndicationStatus.GENERATED,
    iconUrl: CARRIER_LOGOS.geico.url,
    iconAlt: 'Carrier logo',
    carrierName: null,
    truckCount: 1,
    perTruckLow: null,
    perTruckHigh: null,
    fleetLow: null,
    fleetHigh: null,
    carrierNote: null,
    carrierPublicNote: null,
    declineReason: null,
    isCurrentInsurer: false,
    tagline: null,
    ...overrides,
  }
}

const FULL_DETAIL = mockCard({
  id: 'full',
  vendor: Vendor.GEICO,
  vendorName: 'GEICO',
  iconUrl: CARRIER_LOGOS.geico.url,
  iconAlt: CARRIER_LOGOS.geico.alt,
  truckCount: 5,
  perTruckLow: 10200,
  perTruckHigh: 13080,
  fleetLow: 51000,
  fleetHigh: 65400,
  carrierNote: 'Includes a safety discount for a clean 12-month history.',
  carrierPublicNote: 'ELD / dashcam: Required pre-bind — confirm before start.',
  tagline: 'Most competitive',
})

// Progressive web-funnel estimates ride on vendor=ROCKLAKE + a carrier-name
// override for both the display name and the logo (see
// InsuranceEstimateSerializer.get_vendor_name on the backend) — not a
// standalone "Progressive" vendor.
const RENEWAL_MATCH = mockCard({
  id: 'renewal',
  vendor: Vendor.ROCKLAKE,
  vendorName: 'Progressive',
  iconUrl: CARRIER_LOGOS.progressive.url,
  iconAlt: CARRIER_LOGOS.progressive.alt,
  perTruckLow: 11280,
  perTruckHigh: 14400,
  isCurrentInsurer: true,
  tagline: 'Current insurance provider',
})

// Same Rocklake-riding pattern, this time for Canal.
const MINIMAL = mockCard({
  id: 'minimal',
  vendor: Vendor.ROCKLAKE,
  vendorName: 'Canal',
  iconUrl: CARRIER_LOGOS.canal.url,
  iconAlt: CARRIER_LOGOS.canal.alt,
  perTruckLow: 11640,
  perTruckHigh: 14880,
})

const DECLINED_WITH_REASON = mockCard({
  id: 'declined-reason',
  vendor: Vendor.COVERWHALE,
  vendorName: 'Cover Whale',
  iconUrl: CARRIER_LOGOS.coverwhale.url,
  iconAlt: CARRIER_LOGOS.coverwhale.alt,
  status: InsuranceIndicationStatus.DECLINED,
  declineReason: 'Your fleet size exceeds this insurance provider’s maximum for your US state.',
})

const DECLINED_NO_REASON = mockCard({
  id: 'declined-generic',
  vendor: Vendor.NIRVANA,
  vendorName: 'Nirvana Insurance',
  iconUrl: CARRIER_LOGOS.nirvana.url,
  iconAlt: CARRIER_LOGOS.nirvana.alt,
  status: InsuranceIndicationStatus.UNAVAILABLE,
})

const GROUP_BOUNDS = { min: 10200, max: 14880 }

export function CarrierCardSection() {
  const [billing, setBilling] = useState<'annual' | 'monthly'>('monthly')

  return (
    <DsSection
      id="carrier-card"
      title="Carrier estimate card"
      intro={
        <>
          The <code>&lt;CarrierResultCard&gt;</code> component, used on the estimates results page and the New
          Authority start page.
        </>
      }
    >
      <Demo label="Side by side, as shown in the results grid" className="flex flex-col gap-4">
        <Tabs value={billing} onValueChange={(v) => setBilling(v as 'annual' | 'monthly')}>
          <TabsList>
            <TabsTrigger value="annual">Annual</TabsTrigger>
            <TabsTrigger value="monthly">Monthly</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex flex-col gap-4">
          <CarrierResultCard card={FULL_DETAIL} rangeBounds={GROUP_BOUNDS} billing={billing} />
          <CarrierResultCard card={RENEWAL_MATCH} rangeBounds={GROUP_BOUNDS} billing={billing} />
          <CarrierResultCard card={MINIMAL} rangeBounds={GROUP_BOUNDS} billing={billing} />
        </div>
      </Demo>

      <Demo label="Eligible · full detail (fleet breakdown + carrier note)">
        <CarrierResultCard card={FULL_DETAIL} rangeBounds={GROUP_BOUNDS} billing="monthly" />
      </Demo>

      <Demo label="Eligible · current insurance provider">
        <CarrierResultCard card={RENEWAL_MATCH} rangeBounds={GROUP_BOUNDS} billing="monthly" />
      </Demo>

      <Demo label="Eligible · minimal (no tagline, no note, no range bar)">
        <CarrierResultCard card={MINIMAL} rangeBounds={null} billing="monthly" />
      </Demo>

      <Demo label="Declined · with reason">
        <CarrierResultCard card={DECLINED_WITH_REASON} rangeBounds={GROUP_BOUNDS} billing="monthly" />
      </Demo>

      <Demo label="Declined · no reason (fallback copy)">
        <CarrierResultCard card={DECLINED_NO_REASON} rangeBounds={GROUP_BOUNDS} billing="monthly" />
      </Demo>
    </DsSection>
  )
}
